import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import {
  IUnsubscribeGroup,
  CreateUnsubscribeGroupRequest,
  UpdateUnsubscribeGroupRequest,
  GetUnsubscribeGroupsResponse,
} from '../../types/unsubscribeGroups/unsubscribeGroups.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Get all unsubscribe groups
export const getUnsubscribeGroups =
  async (): Promise<GetUnsubscribeGroupsResponse> => {
    try {
      const response = await api.get('/unsubscribe-groups/');
      return response.data;
    } catch (error) {
      console.error('Error fetching unsubscribe groups:', error);
      throw error;
    }
  };

// Get unsubscribe group by ID
export const getUnsubscribeGroupById = async (
  id: string
): Promise<IUnsubscribeGroup> => {
  try {
    const response = await api.get(`/unsubscribe-groups/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching unsubscribe group:', error);
    throw error;
  }
};

// Create unsubscribe group
export const createUnsubscribeGroup = async (
  unsubscribeGroupData: CreateUnsubscribeGroupRequest
): Promise<IUnsubscribeGroup> => {
  try {
    const response = await api.post(
      '/unsubscribe-groups/',
      unsubscribeGroupData
    );
    return response.data.unsubscribeGroup;
  } catch (error) {
    console.error('Error creating unsubscribe group:', error);
    throw error;
  }
};

// Update unsubscribe group
export const updateUnsubscribeGroup = async (
  unsubscribeGroupData: UpdateUnsubscribeGroupRequest
): Promise<IUnsubscribeGroup> => {
  try {
    const response = await api.put(
      `/unsubscribe-groups/${unsubscribeGroupData.id}`,
      unsubscribeGroupData.data
    );
    return response.data;
  } catch (error) {
    console.error('Error updating unsubscribe group:', error);
    throw error;
  }
};

// Delete unsubscribe group
export const deleteUnsubscribeGroup = async (id: string): Promise<void> => {
  try {
    await api.delete(`/unsubscribe-groups/${id}`);
  } catch (error) {
    console.error('Error deleting unsubscribe group:', error);
    throw error;
  }
};

export default api;
